// Using shared api instance instead of direct axios
import api from '@/services/api'
import { useAuthStore } from '@/store/auth'

// Use the shared API instance base URL; defaults to '/api' in production
const API_BASE = (api?.defaults?.baseURL as string) || '/api'

export interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
}

export interface Chat {
  id: string
  title: string
  messages: Message[]
  created_at?: string
  createdAt: Date
  updatedAt: Date
}

export interface ShoppingLink {
  name: string
  store: string
  url: string
  description: string
  price: string
  icon: string
}

export interface StreamingData {
  type: 'token' | 'ai_chunk' | 'formatted_complete' | 'shopping_links' | 'products' | 'images' | 'sources' | 'status' | 'response_start' | 'response_end' | 'response_complete' | 'chat_id' | 'error'
  content?: string
  token?: string
  data?: any
  message?: string
  chat_id?: string
}

export interface StreamingCallbacks {
  onChunk?: (chunk: string) => void
  onShoppingLinks?: (links: ShoppingLink[]) => void
  onProducts?: (products: any[]) => void
  onImages?: (images: any[]) => void
  onSources?: (sources: any[]) => void
  onStatus?: (status: string) => void
  onError?: (error: string) => void
  onChatId?: (chatId: string) => void
}

export const chatService = {
  async getChats(tokenOverride?: string): Promise<Chat[]> {
    const token = tokenOverride || useAuthStore.getState().token || localStorage.getItem('jpdz_token')
    try {
      const headers: Record<string, string> = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const response = await api.get(`/user/chats`, { headers })
      const data = response.data
      // Backend returns { success: true, data: { chats: [] } }
      // Need to handle data.data.chats or data.chats
      const chatsArray = data?.data?.chats || data?.chats
      const raw = Array.isArray(data) ? data : (Array.isArray(chatsArray) ? chatsArray : [])
      const chats = raw.map((c: any) => ({
        id: String(c.id),
        title: c.title || 'New Chat',
        messages: Array.isArray(c.messages) ? c.messages : [],
        created_at: c.created_at,
        createdAt: typeof c.created_at === 'number' ? new Date(c.created_at * 1000) : (c.created_at ? new Date(c.created_at) : new Date()),
        updatedAt: typeof c.updated_at === 'number' ? new Date(c.updated_at * 1000) : (c.updated_at ? new Date(c.updated_at) : new Date()),
      }))
      return chats
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('eeV: Authentication failed')
      }
      throw new Error(`eeV: ${error.message}`)
    }
  },

  async getChatMessages(chatId: string, tokenOverride?: string): Promise<Message[]> {
    const token = tokenOverride || useAuthStore.getState().token || localStorage.getItem('jpdz_token')
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await api.get(`/chat/${chatId}`, { headers })
    const data = response.data
    // Backend returns { success: true, data: { chat: { messages: [] } } }
    const chatData = data?.data?.chat || data?.chat || data
    const messages = Array.isArray(data) ? data : Array.isArray(chatData?.messages) ? chatData.messages : Array.isArray(data?.messages) ? data.messages : []
    return messages
  },

  async createChat(tokenOverride?: string, title: string = 'New Chat'): Promise<Chat> {
    const token = tokenOverride || useAuthStore.getState().token || localStorage.getItem('jpdz_token')

    try {
      const headers: Record<string, string> = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const response = await api.post(`/user/chats`, { title }, { headers })

      const data = response.data || {}

      // Backend returns: { success: true, data: { chat: { id, title, ... } } }
      // So we need data.data.chat
      const chatData = data.data?.chat || data.chat
      if (chatData && chatData.id) {
        const c = chatData
        const created = typeof c.created_at === 'number' ? new Date(c.created_at * 1000) : c.created_at ? new Date(c.created_at) : new Date()
        const updated = typeof c.updated_at === 'number' ? new Date(c.updated_at * 1000) : c.updated_at ? new Date(c.updated_at) : created
        return {
          id: String(c.id),
          title: c.title || title,
          messages: Array.isArray(c.messages) ? c.messages : [],
          created_at: c.created_at,
          createdAt: created,
          updatedAt: updated,
        }
      }

      // Legacy shape: { chat_id: 123 }
      if (data.chat_id) {
        const now = new Date()
        return {
          id: String(data.chat_id),
          title,
          messages: [],
          created_at: now.toISOString(),
          createdAt: now,
          updatedAt: now,
        }
      }

      throw new Error('eeX: Missing chat in response')
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('eeX: Authentication failed')
      }
      throw new Error(`eeX: ${error.message}`)
    }
  },

  async deleteChat(tokenOverride: string | undefined, chatId: string): Promise<void> {
    const token = tokenOverride || useAuthStore.getState().token || localStorage.getItem('jpdz_token')
    try {
      const headers: Record<string, string> = {}
      if (token) headers.Authorization = `Bearer ${token}`
      await api.delete(`/user/chats/${chatId}`, { headers })
      return
    } catch (err: any) {
      // Try alternative path used by some backends
      try {
        const headers2: Record<string, string> = {}
        if (token) headers2.Authorization = `Bearer ${token}`
        await api.delete(`/chats/${chatId}`, { headers: headers2 })
        return
      } catch (err2: any) {
        // If it's a 404, consider it already gone and succeed optimistically
        const status = err2?.response?.status || err?.response?.status
        if (status === 404) return
        throw err2 || err
      }
    }
  },

  async streamMessage(
    query: string,
    onChunk: (chunk: any) => void,
    onComplete: () => void,
    chatId?: string | null
  ): Promise<void> {
    const token = useAuthStore.getState().token || localStorage.getItem('jpdz_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await fetch(`${API_BASE}/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, chat_id: chatId }),
      // Ensure cookies (HttpOnly jpdz_token) are sent with the request
      credentials: 'include',
    })


    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 405) {
      // Fallback chain: prefer /jpdz/search (MCP + SearxNG), then /chat as last resort
      try {
        const fb2 = await api.post('/jpdz/search', { query, user_context: {}, stream: false, include_images: true }, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = fb2?.data || {}
        const text2 = data.response || data.text || data.message || ''
        if (text2) {
          onChunk({ type: 'token', content: text2 })
          if (Array.isArray(data?.shopping_links)) {
            onChunk({ type: 'shopping_links', data: data.shopping_links })
          }
          if (Array.isArray(data?.sources)) {
            onChunk({ type: 'sources', data: data.sources })
          }
          onComplete()
          return
        }
      } catch { }
      try {
        const fb1 = await api.post('/chat', { query, chat_id: chatId }, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const text1 = fb1?.data?.response || ''
        if (text1) {
          onChunk({ type: 'token', content: text1 })
          onComplete()
          return
        }
      } catch { }
      throw new Error('Failed to stream chat')
    }

    if (!response.ok || !response.body) {
      throw new Error('Failed to stream chat')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8', { fatal: false, ignoreBOM: true })
    let partial = ''

    // Continuously read from the stream
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      if (!value) continue

      try {
        // Decode with error recovery
        partial += decoder.decode(value, { stream: true })
      } catch (error) {
        // Try to decode with replacement characters
        const fallbackDecoder = new TextDecoder('utf-8', { fatal: false })
        partial += fallbackDecoder.decode(value, { stream: true })
      }

      // Split by new lines for SSE format
      const lines = partial.split('\n\n')
      // Leave the last partial line for the next chunk if it doesn't end with \n\n
      partial = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) {
          // Skip non-SSE format lines
          continue
        }
        const jsonStr = line.replace(/^data: /, '').trim()
        if (!jsonStr) continue
        try {
          const payload: StreamingData = JSON.parse(jsonStr)

          // Handle different types of streaming data
          switch (payload.type) {
            case 'token':
              if (payload.content) {
                onChunk({ type: 'token', content: payload.content })
              }
              break
            case 'formatted_complete':
              if (payload.content) {
                onChunk({ type: 'formatted_complete', content: payload.content })
              }
              break
            case 'ai_chunk':
              let chunkContent = ''
              if (payload.content && typeof payload.content === 'string') {
                chunkContent = payload.content
              } else if (payload.token && typeof payload.token === 'string') {
                chunkContent = payload.token
              }

              if (chunkContent) {
                onChunk({ type: 'token', content: chunkContent })
              }
              break
            case 'products':
              if (payload.data) {
                onChunk({ type: 'products', data: payload.data })
              }
              break

            case 'shopping_links':
              if (payload.data) {
                onChunk({ type: 'shopping_links', data: payload.data })
              }
              break
            case 'images':
              if (payload.data) {
                onChunk({ type: 'images', data: payload.data })
              }
              break
            case 'sources':
              if (payload.data) {
                onChunk({ type: 'sources', data: payload.data })
              }
              break
            case 'status':
              if (payload.message) {
                onChunk({ type: 'status', message: payload.message })
              }
              break
            case 'error':
              if (payload.message) {
                onChunk({ type: 'error', message: payload.message })
              }
              break
            case 'chat_id':
              if (payload.chat_id) {
                onChunk({ type: 'chat_id', chat_id: payload.chat_id })
              }
              break
            case 'response_start':
            case 'response_end':
            case 'response_complete':
              if (payload.type === 'response_complete') {
                onComplete()
              }
              break
            default:
              // For unknown types, only pass through content if it's clearly meant for display
              if (payload.content && typeof payload.content === 'string') {
                onChunk({ type: 'token', content: payload.content })
              }
          }
        } catch (err) {
          // Enhanced malformed JSON handling

          // Try to extract content from malformed JSON
          let extractedContent = this.extractContentFromMalformedJSON(jsonStr)

          if (extractedContent) {
            onChunk({ type: 'token', content: extractedContent })
          }
        }
      }
    }
  },

  // Filter raw content to remove technical SSE events and properly format content for display
  filterRawContent(content: string): string | null {
    if (!content || typeof content !== 'string') {
      return null
    }

    // Remove common SSE technical events that shouldn't be displayed
    const technicalPatterns = [
      /^connection$/i,
      /^complete$/i,
      /^response_start$/i,
      /^response_end$/i,
      /^response_complete$/i,
      /^status:/i,
      /^data:\s*$/i,
      /^\s*\{\s*"type"\s*:\s*"(connection|complete|response_start|response_end|response_complete|status)"/i,
    ]

    // Check if content matches technical patterns that should be filtered out
    for (const pattern of technicalPatterns) {
      if (pattern.test(content.trim())) {
        return null
      }
    }

    // Handle structured JSON data that shouldn't be displayed as raw text
    if (content.includes('"type"')) {
      try {
        const parsed = JSON.parse(content)
        // Filter out structured data types that should be handled by specific callbacks
        if (parsed.type && ['shopping_links', 'products', 'images', 'sources', 'status', 'error', 'response_start', 'response_end', 'response_complete'].includes(parsed.type)) {
          return null
        }

        // Handle ai_chunk specifically - extract the actual content
        if (parsed.type === 'ai_chunk') {
          if (parsed.content && typeof parsed.content === 'string') {
            return this.cleanTextContent(parsed.content)
          } else if (parsed.token && typeof parsed.token === 'string') {
            return this.cleanTextContent(parsed.token)
          }
          return null
        }
      } catch (e) {
        // If JSON parsing fails, continue with text extraction
      }
    }

    // Handle shopping links arrays that appear as raw JSON
    if (content.includes('shopping_links') || content.includes('"store"') || content.includes('"price"')) {
      return null // Shopping links should be handled by the onShoppingLinks callback
    }

    // Extract and clean text content
    return this.cleanTextContent(content)
  },

  // Clean and validate text content for display
  cleanTextContent(content: string): string | null {
    if (!content || typeof content !== 'string') {
      return null
    }

    let cleanedContent = content

    // Remove JSON wrapper artifacts
    cleanedContent = cleanedContent.replace(/^\s*\{\s*"content"\s*:\s*"/, '')
    cleanedContent = cleanedContent.replace(/"\s*\}\s*$/, '')

    // Remove other common JSON artifacts
    cleanedContent = cleanedContent.replace(/^\s*"/, '').replace(/"\s*$/, '')

    // Unescape JSON strings
    cleanedContent = cleanedContent.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\')

    // Remove empty or whitespace-only content
    if (!cleanedContent.trim()) {
      return null
    }

    // Remove content that's just technical tokens or JSON artifacts
    if (cleanedContent.trim().length < 2 && /^[{}\[\]",:\s]*$/.test(cleanedContent)) {
      return null
    }

    // Filter out remaining JSON-like structures that shouldn't be displayed
    if (cleanedContent.includes('{"') && cleanedContent.includes('"}') && cleanedContent.length < 100) {
      return null
    }

    return cleanedContent.trim()
  },

  // Enhanced content extraction from malformed JSON
  extractContentFromMalformedJSON(jsonStr: string): string | null {
    if (!jsonStr || typeof jsonStr !== 'string') return null

    // Method 1: Try regex extraction for common patterns
    const patterns = [
      /"content":\s*"([^"]*(?:\\.[^"]*)*)"/,
      /"token":\s*"([^"]*(?:\\.[^"]*)*)"/,
      /"message":\s*"([^"]*(?:\\.[^"]*)*)"/,
      /"text":\s*"([^"]*(?:\\.[^"]*)*)"/,
      /"data":\s*"([^"]*(?:\\.[^"]*)*)"/
    ]

    for (const pattern of patterns) {
      const match = jsonStr.match(pattern)
      if (match) {
        let content = match[1]
        // Enhanced JSON unescaping
        content = content
          .replace(/\\"/g, '"')
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t')
          .replace(/\\\\/g, '\\')
          .replace(/\\\//g, '/')

        // Handle Unicode escapes
        content = content.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => {
          return String.fromCharCode(parseInt(hex, 16))
        })

        if (content.trim()) return content
      }
    }

    // Method 2: Try partial JSON parsing
    try {
      const colonIndex = jsonStr.indexOf(':')
      const lastQuoteIndex = jsonStr.lastIndexOf('"')
      if (colonIndex !== -1 && lastQuoteIndex > colonIndex) {
        const contentPart = jsonStr.substring(colonIndex + 1, lastQuoteIndex)
        const cleaned = contentPart.replace(/^["'\s]+|["'\s]+$/g, '')
        if (cleaned.trim()) return cleaned
      }
    } catch (e) {
    }

    // Method 3: Extract any quoted content
    try {
      const quoteMatches = jsonStr.match(/"([^"]{3,})"/g)
      if (quoteMatches) {
        for (const match of quoteMatches) {
          const content = match.slice(1, -1) // Remove quotes
          // Skip obvious JSON keys
          if (['content', 'token', 'message', 'text', 'data', 'type', 'status'].includes(content.toLowerCase())) {
            continue
          }
          if (content.trim()) return content
        }
      }
    } catch (e) {
    }

    return null
  },

  // Fix token spacing issues in content
  fixTokenSpacing(content: string): string {
    if (!content || typeof content !== 'string') return content

    // Fix missing spaces after punctuation
    content = content.replace(/([.!?])([A-Z])/g, '$1 $2')

    // Fix missing spaces after commas
    content = content.replace(/,([A-Za-z])/g, ', $1')

    // Fix concatenated words (common in model outputs)
    content = content.replace(/([a-z])([A-Z][a-z])/g, '$1 $2')

    // Fix missing spaces around parentheses
    content = content.replace(/([a-zA-Z])\(/g, '$1 (')
    content = content.replace(/\)([a-zA-Z])/g, ') $1')

    // Fix numbers and units
    content = content.replace(/(\d)([A-Za-z])/g, '$1 $2')

    // Fix concatenated sentences (no space after period)
    content = content.replace(/([a-z])\.([A-Z])/g, '$1. $2')

    // Fix missing spaces around colons
    content = content.replace(/([a-zA-Z]):([a-zA-Z])/g, '$1: $2')

    // Fix missing spaces around semicolons
    content = content.replace(/([a-zA-Z]);([a-zA-Z])/g, '$1; $2')

    // Fix common word boundaries that get lost
    content = content.replace(/(the|and|or|but|for|with|to|in|on|at|by)([A-Z][a-z])/g, '$1 $2')
    content = content.replace(/([a-z])(The|And|Or|But|For|With|To|In|On|At|By)/g, '$1 $2')

    // Clean up multiple spaces
    content = content.replace(/\s+/g, ' ')

    return content.trim()
  },
}
